import { useState, useEffect, useCallback } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSTextField from "views/system-design/DSTextField";
import DSPaper from "views/system-design/DSPaper";
import DSIconButton from "views/system-design/DSIconButton";
import DSFormControl from "views/system-design/DSFormControl";
import DSInputLabel from "views/system-design/DSInputLabel";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import useStudioGuides from "../hooks/useStudioGuides";
import useSaveStudioGuides from "../hooks/useSaveStudioGuides";
import {
  StudioGuide,
  GuideSection,
  GuideBullet,
  GuideLink,
  GuideDisplayMode,
} from "api/admin";

type SectionDraft = GuideSection & { _key: string };
type GuideDraft = Omit<StudioGuide, "sections"> & {
  _key: string;
  sections: SectionDraft[];
};

const newKey = () => crypto.randomUUID();

const toDrafts = (items: StudioGuide[]): GuideDraft[] =>
  items.map((item) => ({
    ...item,
    _key: newKey(),
    sections: (item.sections ?? []).map((section) => ({
      ...section,
      _key: newKey(),
    })),
  }));

const toGuides = (drafts: GuideDraft[]): StudioGuide[] =>
  drafts.map(({ _key, sections, ...guide }) => ({
    ...guide,
    ...(guide.displayMode === "sections"
      ? { sections: sections.map(({ _key: _sk, ...section }) => section) }
      : {}),
  }));

const emptySection = (): SectionDraft => ({
  _key: newKey(),
  heading: "",
});

const emptyGuide = (): GuideDraft => ({
  _key: newKey(),
  id: `guide-${newKey().slice(0, 8)}`,
  title: "",
  subtitle: "",
  displayMode: "sections",
  sections: [emptySection()],
});

type GuideSectionsEditorProps = {
  guide: GuideDraft;
  updateGuide: (
    guideKey: string,
    updater: (guide: GuideDraft) => GuideDraft,
  ) => void;
  updateSection: (
    guideKey: string,
    sectionKey: string,
    updater: (section: SectionDraft) => SectionDraft,
  ) => void;
};

const GuideSectionsEditor = ({
  guide,
  updateGuide,
  updateSection,
}: GuideSectionsEditorProps) => {
  const handleAddSection = () => {
    updateGuide(guide._key, (g) => ({
      ...g,
      sections: [...g.sections, emptySection()],
    }));
  };

  const handleRemoveSection = (sectionKey: string) => {
    updateGuide(guide._key, (g) => ({
      ...g,
      sections: g.sections.filter((section) => section._key !== sectionKey),
    }));
  };

  const handleParagraphChange = (
    sectionKey: string,
    paragraphIndex: number,
    value: string,
  ) => {
    updateSection(guide._key, sectionKey, (section) => ({
      ...section,
      paragraphs: (section.paragraphs ?? []).map((paragraph, i) =>
        i === paragraphIndex ? value : paragraph,
      ),
    }));
  };

  const handleBulletChange = (
    sectionKey: string,
    bulletIndex: number,
    field: keyof GuideBullet,
    value: string,
  ) => {
    updateSection(guide._key, sectionKey, (section) => ({
      ...section,
      bullets: (section.bullets ?? []).map((bullet, i) =>
        i === bulletIndex ? { ...bullet, [field]: value } : bullet,
      ),
    }));
  };

  const handleNoteLinkChange = (
    sectionKey: string,
    linkIndex: number,
    field: keyof GuideLink,
    value: string,
  ) => {
    updateSection(guide._key, sectionKey, (section) => ({
      ...section,
      noteLinks: (section.noteLinks ?? []).map((link, i) =>
        i === linkIndex ? { ...link, [field]: value } : link,
      ),
    }));
  };

  const handleBodyLinkChange = (
    sectionKey: string,
    linkIndex: number,
    field: keyof GuideLink,
    value: string,
  ) => {
    updateSection(guide._key, sectionKey, (section) => ({
      ...section,
      links: (section.links ?? []).map((link, i) =>
        i === linkIndex ? { ...link, [field]: value } : link,
      ),
    }));
  };

  // Body links anchor into paragraph/bullet text, so drop them once no body
  // text remains — otherwise the API silently discards them on save (it only
  // keeps body links when the section still has paragraphs or bullets).
  const pruneBodyLinks = (section: SectionDraft): SectionDraft => {
    if (section.paragraphs?.length || section.bullets?.length) return section;
    if (!section.links?.length) return section;
    const pruned = { ...section };
    delete pruned.links;
    return pruned;
  };

  return (
    <DSBox sx={{ display: "flex", flexDirection: "column", gap: 1.5, pl: 1.5 }}>
      <Droppable droppableId={`sections-${guide._key}`} type="SECTION">
        {(provided) => (
          <DSBox
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            {guide.sections.map((section, sectionIndex) => (
              <Draggable
                key={section._key}
                draggableId={section._key}
                index={sectionIndex}
              >
                {(dragProvided, snapshot) => (
                  <DSPaper
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      boxShadow: snapshot.isDragging ? 6 : 0,
                      bgcolor: snapshot.isDragging
                        ? "action.hover"
                        : "transparent",
                    }}
                  >
                    <DSBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <DSBox
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <DSBox
                          {...dragProvided.dragHandleProps}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "grab",
                            color: "text.secondary",
                          }}
                        >
                          <DragIndicatorIcon fontSize="small" />
                        </DSBox>
                        <DSTypography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ flex: 1 }}
                        >
                          Section {sectionIndex + 1}
                        </DSTypography>
                        <DSIconButton
                          onClick={() => handleRemoveSection(section._key)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </DSIconButton>
                      </DSBox>
                      <DSTextField
                        label="Heading"
                        value={section.heading}
                        onChange={(e) =>
                          updateSection(guide._key, section._key, (s) => ({
                            ...s,
                            heading: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                      {(section.paragraphs ?? []).map(
                        (paragraph, paragraphIndex) => (
                          <DSBox
                            // eslint-disable-next-line react/no-array-index-key
                            key={paragraphIndex}
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "flex-start",
                            }}
                          >
                            <DSTextField
                              label={`Paragraph ${paragraphIndex + 1}`}
                              value={paragraph}
                              onChange={(e) =>
                                handleParagraphChange(
                                  section._key,
                                  paragraphIndex,
                                  e.target.value,
                                )
                              }
                              fullWidth
                              multiline
                              rows={2}
                            />
                            <DSIconButton
                              onClick={() =>
                                updateSection(guide._key, section._key, (s) =>
                                  pruneBodyLinks({
                                    ...s,
                                    paragraphs: (s.paragraphs ?? []).filter(
                                      (_, i) => i !== paragraphIndex,
                                    ),
                                  }),
                                )
                              }
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </DSIconButton>
                          </DSBox>
                        ),
                      )}
                      {(section.bullets ?? []).map((bullet, bulletIndex) => (
                        <DSBox
                          // eslint-disable-next-line react/no-array-index-key
                          key={bulletIndex}
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <DSTextField
                            label="Bullet lead (bold)"
                            value={bullet.lead ?? ""}
                            onChange={(e) =>
                              handleBulletChange(
                                section._key,
                                bulletIndex,
                                "lead",
                                e.target.value,
                              )
                            }
                            sx={{ flex: 1 }}
                          />
                          <DSTextField
                            label="Bullet text"
                            value={bullet.text}
                            onChange={(e) =>
                              handleBulletChange(
                                section._key,
                                bulletIndex,
                                "text",
                                e.target.value,
                              )
                            }
                            sx={{ flex: 2 }}
                          />
                          <DSIconButton
                            onClick={() =>
                              updateSection(guide._key, section._key, (s) =>
                                pruneBodyLinks({
                                  ...s,
                                  bullets: (s.bullets ?? []).filter(
                                    (_, i) => i !== bulletIndex,
                                  ),
                                }),
                              )
                            }
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </DSIconButton>
                        </DSBox>
                      ))}
                      {(section.links ?? []).map((link, linkIndex) => (
                        <DSBox
                          // eslint-disable-next-line react/no-array-index-key
                          key={linkIndex}
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "flex-start",
                            pl: 1.5,
                          }}
                        >
                          <DSTextField
                            label="Body link text"
                            helperText="Must appear verbatim in a paragraph or bullet"
                            value={link.text}
                            onChange={(e) =>
                              handleBodyLinkChange(
                                section._key,
                                linkIndex,
                                "text",
                                e.target.value,
                              )
                            }
                            sx={{ flex: 1 }}
                          />
                          <DSTextField
                            label="Body link URL"
                            helperText="Paste a klydo URL (…/public/klydo/<id>) to open a preview popup, or any link"
                            value={link.url}
                            onChange={(e) =>
                              handleBodyLinkChange(
                                section._key,
                                linkIndex,
                                "url",
                                e.target.value,
                              )
                            }
                            sx={{ flex: 2 }}
                          />
                          <DSIconButton
                            onClick={() =>
                              updateSection(guide._key, section._key, (s) => ({
                                ...s,
                                links: (s.links ?? []).filter(
                                  (_, i) => i !== linkIndex,
                                ),
                              }))
                            }
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </DSIconButton>
                        </DSBox>
                      ))}
                      <DSTextField
                        label="Note (highlighted callout)"
                        value={section.note ?? ""}
                        onChange={(e) =>
                          updateSection(guide._key, section._key, (s) => {
                            const updated = { ...s };
                            if (e.target.value) {
                              updated.note = e.target.value;
                            } else {
                              // Note links anchor into the note text, so drop
                              // them when the note itself is cleared.
                              delete updated.note;
                              delete updated.noteLinks;
                            }
                            return updated;
                          })
                        }
                        fullWidth
                        multiline
                        rows={2}
                      />
                      {(section.noteLinks ?? []).map((link, linkIndex) => (
                        <DSBox
                          // eslint-disable-next-line react/no-array-index-key
                          key={linkIndex}
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                            pl: 1.5,
                          }}
                        >
                          <DSTextField
                            label="Note link text"
                            helperText="Must appear verbatim in the note"
                            value={link.text}
                            onChange={(e) =>
                              handleNoteLinkChange(
                                section._key,
                                linkIndex,
                                "text",
                                e.target.value,
                              )
                            }
                            sx={{ flex: 1 }}
                          />
                          <DSTextField
                            label="Note link URL"
                            value={link.url}
                            onChange={(e) =>
                              handleNoteLinkChange(
                                section._key,
                                linkIndex,
                                "url",
                                e.target.value,
                              )
                            }
                            sx={{ flex: 2 }}
                          />
                          <DSIconButton
                            onClick={() =>
                              updateSection(guide._key, section._key, (s) => ({
                                ...s,
                                noteLinks: (s.noteLinks ?? []).filter(
                                  (_, i) => i !== linkIndex,
                                ),
                              }))
                            }
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </DSIconButton>
                        </DSBox>
                      ))}
                      <DSBox sx={{ display: "flex", gap: 1 }}>
                        <DSSecondaryButton
                          onClick={() =>
                            updateSection(guide._key, section._key, (s) => ({
                              ...s,
                              paragraphs: [...(s.paragraphs ?? []), ""],
                            }))
                          }
                          startIcon={<AddIcon />}
                          size="small"
                        >
                          Add Paragraph
                        </DSSecondaryButton>
                        <DSSecondaryButton
                          onClick={() =>
                            updateSection(guide._key, section._key, (s) => ({
                              ...s,
                              bullets: [
                                ...(s.bullets ?? []),
                                { text: "" } as GuideBullet,
                              ],
                            }))
                          }
                          startIcon={<AddIcon />}
                          size="small"
                        >
                          Add Bullet
                        </DSSecondaryButton>
                        <DSSecondaryButton
                          disabled={
                            !(
                              section.paragraphs?.length ||
                              section.bullets?.length
                            )
                          }
                          onClick={() =>
                            updateSection(guide._key, section._key, (s) => ({
                              ...s,
                              links: [
                                ...(s.links ?? []),
                                { text: "", url: "" } as GuideLink,
                              ],
                            }))
                          }
                          startIcon={<AddIcon />}
                          size="small"
                        >
                          Add Body Link
                        </DSSecondaryButton>
                        <DSSecondaryButton
                          disabled={!section.note}
                          onClick={() =>
                            updateSection(guide._key, section._key, (s) => ({
                              ...s,
                              noteLinks: [
                                ...(s.noteLinks ?? []),
                                { text: "", url: "" } as GuideLink,
                              ],
                            }))
                          }
                          startIcon={<AddIcon />}
                          size="small"
                        >
                          Add Note Link
                        </DSSecondaryButton>
                      </DSBox>
                    </DSBox>
                  </DSPaper>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </DSBox>
        )}
      </Droppable>
      <DSBox>
        <DSSecondaryButton
          onClick={handleAddSection}
          startIcon={<AddIcon />}
          size="small"
        >
          Add Section
        </DSSecondaryButton>
      </DSBox>
    </DSBox>
  );
};

const StudioGuidesEditor = () => {
  const { data, isPending: isLoading, error } = useStudioGuides();
  const { mutate: save, isPending: isSaving } = useSaveStudioGuides();
  const [guides, setGuides] = useState<GuideDraft[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    // Don't clobber in-progress edits when the query refetches (e.g. on
    // window refocus); only seed from the server when there's nothing unsaved.
    if (data?.items && !hasChanges) {
      setGuides(toDrafts(data.items));
    }
  }, [data?.items]);

  const updateGuide = (
    guideKey: string,
    updater: (guide: GuideDraft) => GuideDraft,
  ) => {
    setGuides((prev) =>
      prev.map((guide) => (guide._key === guideKey ? updater(guide) : guide)),
    );
    setHasChanges(true);
  };

  const updateSection = (
    guideKey: string,
    sectionKey: string,
    updater: (section: SectionDraft) => SectionDraft,
  ) => {
    updateGuide(guideKey, (guide) => ({
      ...guide,
      sections: guide.sections.map((section) =>
        section._key === sectionKey ? updater(section) : section,
      ),
    }));
  };

  const toggleExpanded = (guideKey: string) => {
    setExpandedKeys((prev) =>
      prev.includes(guideKey)
        ? prev.filter((key) => key !== guideKey)
        : [...prev, guideKey],
    );
  };

  const handleAddGuide = () => {
    const guide = emptyGuide();
    setGuides((prev) => [...prev, guide]);
    setExpandedKeys((prev) => [...prev, guide._key]);
    setHasChanges(true);
  };

  const handleRemoveGuide = (guideKey: string) => {
    setGuides((prev) => prev.filter((guide) => guide._key !== guideKey));
    setHasChanges(true);
  };

  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    if (type === "GUIDE") {
      setGuides((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(source.index, 1);
        updated.splice(destination.index, 0, moved);
        return updated;
      });
      setHasChanges(true);
      return;
    }

    // Sections reorder within a single guide only
    if (source.droppableId !== destination.droppableId) return;
    const guideKey = source.droppableId.replace("sections-", "");
    setGuides((prev) =>
      prev.map((guide) => {
        if (guide._key !== guideKey) return guide;
        const sections = [...guide.sections];
        const [moved] = sections.splice(source.index, 1);
        sections.splice(destination.index, 0, moved);
        return { ...guide, sections };
      }),
    );
    setHasChanges(true);
  }, []);

  const handleSave = () => {
    setSaveError(null);
    const invalid = guides.find(
      (guide) =>
        !guide.title.trim() ||
        (guide.displayMode === "sections" &&
          (guide.sections.length === 0 ||
            guide.sections.some((section) => !section.heading.trim()))),
    );
    if (invalid) {
      setSaveError(
        "Every guide needs a title, and every section of a sections guide needs a heading.",
      );
      return;
    }
    save(toGuides(guides), {
      onSuccess: () => setHasChanges(false),
      onError: () => setSaveError("Failed to save guides. Please try again."),
    });
  };

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (error) {
    return (
      <DSBox sx={{ p: 3 }}>
        <DSTypography color="error">
          Failed to load guides: {error.message}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <DSTypography variant="h6">Studio Guides</DSTypography>
        <DSBox sx={{ display: "flex", gap: 1 }}>
          <DSSecondaryButton onClick={handleAddGuide} startIcon={<AddIcon />}>
            Add Guide
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? "Saving..." : "Save Guides"}
          </DSPrimaryButton>
        </DSBox>
      </DSBox>
      {saveError && <DSTypography color="error">{saveError}</DSTypography>}
      {guides.length === 0 && (
        <DSTypography
          color="text.secondary"
          sx={{ textAlign: "center", py: 4 }}
        >
          No guides yet. Click "Add Guide" to get started.
        </DSTypography>
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="guides" type="GUIDE">
          {(provided) => (
            <DSBox
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              {guides.map((guide, guideIndex) => {
                const expanded = expandedKeys.includes(guide._key);
                return (
                  <Draggable
                    key={guide._key}
                    draggableId={guide._key}
                    index={guideIndex}
                  >
                    {(dragProvided, snapshot) => (
                      <DSPaper
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        sx={{
                          p: 2,
                          boxShadow: snapshot.isDragging ? 6 : 1,
                          bgcolor: snapshot.isDragging
                            ? "action.hover"
                            : "background.paper",
                        }}
                      >
                        <DSBox
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <DSBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <DSBox
                              {...dragProvided.dragHandleProps}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "grab",
                                color: "text.secondary",
                              }}
                            >
                              <DragIndicatorIcon />
                            </DSBox>
                            <DSTypography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ flexShrink: 0 }}
                            >
                              Guide {guideIndex + 1}
                            </DSTypography>
                            <DSTypography
                              variant="subtitle1"
                              sx={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {guide.title || "Untitled guide"}
                            </DSTypography>
                            <DSTypography
                              variant="body2"
                              color="text.secondary"
                            >
                              {guide.displayMode === "slides"
                                ? "Slides"
                                : `${guide.sections.length} sections`}
                            </DSTypography>
                            <DSIconButton
                              onClick={() => toggleExpanded(guide._key)}
                              size="small"
                            >
                              {expanded ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </DSIconButton>
                            <DSIconButton
                              onClick={() => handleRemoveGuide(guide._key)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </DSIconButton>
                          </DSBox>
                          {expanded && (
                            <DSBox
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              <DSBox sx={{ display: "flex", gap: 1 }}>
                                <DSTextField
                                  label="Title"
                                  value={guide.title}
                                  onChange={(e) =>
                                    updateGuide(guide._key, (g) => ({
                                      ...g,
                                      title: e.target.value,
                                    }))
                                  }
                                  sx={{ flex: 2 }}
                                />
                                <DSFormControl sx={{ flex: 1 }} size="small">
                                  <DSInputLabel>Display</DSInputLabel>
                                  <DSSelect
                                    label="Display"
                                    value={guide.displayMode}
                                    onChange={(e) =>
                                      updateGuide(guide._key, (g) => ({
                                        ...g,
                                        displayMode: e.target
                                          .value as GuideDisplayMode,
                                        sections:
                                          g.sections.length === 0
                                            ? [emptySection()]
                                            : g.sections,
                                      }))
                                    }
                                  >
                                    <DSMenuItem value="sections">
                                      Sections reader
                                    </DSMenuItem>
                                    <DSMenuItem value="slides">
                                      Slides carousel
                                    </DSMenuItem>
                                  </DSSelect>
                                </DSFormControl>
                              </DSBox>
                              <DSTextField
                                label="Subtitle"
                                value={guide.subtitle}
                                onChange={(e) =>
                                  updateGuide(guide._key, (g) => ({
                                    ...g,
                                    subtitle: e.target.value,
                                  }))
                                }
                                fullWidth
                              />
                              {guide.displayMode === "slides" ? (
                                <DSTypography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Slides guides show the images from the
                                  Onboarding tab — manage them there.
                                </DSTypography>
                              ) : (
                                <GuideSectionsEditor
                                  guide={guide}
                                  updateGuide={updateGuide}
                                  updateSection={updateSection}
                                />
                              )}
                            </DSBox>
                          )}
                        </DSBox>
                      </DSPaper>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </DSBox>
          )}
        </Droppable>
      </DragDropContext>
    </DSBox>
  );
};

export default StudioGuidesEditor;
